import React from 'react'
import styled from 'styled-components'
import { Box, Flex, Image } from 'ui/acro'
import { P } from 'ui/bend'
import CommonLink from 'components_v2/CommonLink'
import { community_event_card_click_event } from 'gtm/pushEvents'
import AddToCalendar from '../AddToCalendar'
import CancelRegistration from '../CancelRegistration'
import {
  formatEventDate,
  formatEventLocation,
  formatEventTimeRange,
  isSameEventDay
} from '../../../components_v2/Events/formatEvent'
import { ExtendedDateComponent, LocationIcon } from '../EventDetailView.style'

const CANCELLABLE_STATUSES = ['registered', 'waitlisted', 'pending']

const PLACEHOLDER_IMAGE =
  'https://alomoves.s3.amazonaws.com/manual_uploads/shared/search/placeholder-cards/rectangle-card.jpg'

const TextContainer = styled(Flex)({
  flexDirection: 'column',
  bottom: '16px',
  left: '16px',
  gap: '5px',
  width: '50%',
  flex: '1',
  zIndex: '10',
  justifyContent: 'space-between',
  '@media (max-width: 768px)': {
    flex: '0 0 55%'
  }
})

const Title = styled('div')({
  color: '#000',
  fontSize: '14px',
  fontWeight: 700,
  fontFamily: 'Proxima-nova',
  textTransform: 'uppercase',
  lineHeight: '18px',
  marginBottom: '12px',

  display: '-webkit-box',
  WebkitBoxOrient: 'vertical',
  WebkitLineClamp: 2,
  overflow: 'hidden'
})

const Main = styled(Flex)`
  gap: 12px;
  margin: 4px 0 0 0;
  min-height: 220px;
  margin-top: 16px;

  @media (max-width: 768px) {
    width: 100% !important;
  }

  @media (max-width: 500px) {
    align-items: flex-start;
  }
`

const StyledBox = styled(Box)`
  flex: 1;
  position: relative;
  flex: 0 0 40%;

  .event-image {
    left: 0;
    object-fit: cover;
    top: 0;
    aspect-ratio: 1 / 1;
    height: auto;
  }

  @media screen and (min-width: 768px) {
    flex: 0 0 40%;
    .event-image {
      aspect-ratio: 263 / 175;
      height: 100%;
      max-height: 210px;
    }
  }
  .past-overlay {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-image: linear-gradient(
      rgba(255, 255, 255, 0.5),
      rgba(255, 255, 255, 0.5)
    );
    pointer-events: none;
  }
`

const Meta = styled(P)({
  fontSize: '14px',
  lineHeight: '150%',
  display: 'flex',
  gap: '8px'
})

const FirstBox = styled.div``

const SecondBox = styled.div`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  width: fit-content;
`

const calenderIcon = (
  <svg
    xmlns='http://www.w3.org/2000/svg'
    width='20'
    height='20'
    viewBox='0 0 20 20'
    fill='none'
  >
    <path
      d='M6.5 2H13.5V0H14.5V2H19V19H1V2H5.5V0H6.5V2ZM2 7.5V18H18V7.5H2ZM2 6.5H18V3H14.5V5H13.5V3H6.5V5H5.5V3H2V6.5Z'
      fill='black'
    />
  </svg>
)

const timeIcon = (
  <svg
    xmlns='http://www.w3.org/2000/svg'
    width='20'
    height='20'
    viewBox='0 0 20 20'
    fill='none'
  >
    <path
      d='M10 1C14.9706 1 19 5.02944 19 10C19 14.9706 14.9706 19 10 19C5.02944 19 1 14.9706 1 10C1 5.02944 5.02944 1 10 1ZM10 2C5.58172 2 2 5.58172 2 10C2 14.4183 5.58172 18 10 18C14.4183 18 18 14.4183 18 10C18 5.58172 14.4183 2 10 2ZM10 9.29297L13.8535 13.1465L13.1465 13.8535L9 9.70703V4.5H10V9.29297Z'
      fill='black'
    />
  </svg>
)

const EventCarouselCard = (props) => {
  const {
    id,
    title,
    coverUrl,
    startAt,
    endAt,
    timezone,
    city,
    region,
    venue,
    locationType,
    registrationStatus,
    onEventCancelled,
    location,
    position
  } = props

  const dateLabel = formatEventDate(startAt, timezone)
  const timeLabel = formatEventTimeRange(startAt, endAt, timezone)
  const locationLabel = formatEventLocation({
    city,
    region,
    venue,
    locationType
  })
  const detailPath = id ? `/events/${id}` : '#'
  const href = detailPath

  const isPast = new Date(startAt).getTime() < Date.now()
  const isSameDay = isSameEventDay(startAt, endAt, timezone)
  const canCancel =
    !isPast &&
    Boolean(id) &&
    (registrationStatus
      ? CANCELLABLE_STATUSES.includes(registrationStatus)
      : true)

  const handleCardClick = () => {
    community_event_card_click_event({
      event: { id, title },
      location,
      position
    })
  }

  return (
    <Main
      as={CommonLink}
      className={'swiper-slide unstyled'}
      to={href}
      onClick={handleCardClick}
    >
      <StyledBox mb={['8px', '10px']}>
        <Image src={coverUrl || PLACEHOLDER_IMAGE} className='event-image' />
        {isPast && <div className='past-overlay' />}
      </StyledBox>

      <TextContainer>
        <FirstBox>
          <Title>{title}</Title>
          <Meta>
            <div>{calenderIcon}</div>
            {dateLabel && (
              <Meta>
                {isSameDay ? (
                  <p>{dateLabel}</p>
                ) : (
                  <ExtendedDateComponent
                    startAt={startAt}
                    endAt={endAt}
                    timezone={timezone}
                  />
                )}
              </Meta>
            )}
          </Meta>
          <Meta>
            <div>
              <LocationIcon />
            </div>
            {locationLabel && <Meta>{locationLabel}</Meta>}
          </Meta>
          {isSameDay && (
            <Meta>
              <div>{timeIcon}</div>
              {timeLabel && <Meta>{timeLabel}</Meta>}
            </Meta>
          )}
        </FirstBox>
        <SecondBox>
          {!isPast && <AddToCalendar {...props} />}
          {canCancel && (
            <CancelRegistration
              id={id}
              title={title}
              registrationStatus={registrationStatus}
              location={location}
              onEventCancelled={onEventCancelled}
            />
          )}
        </SecondBox>
      </TextContainer>
    </Main>
  )
}

export default EventCarouselCard
